<script setup>
import { useInfiniteScroll } from "@vueuse/core";
import { ref } from "vue";

const config = useRuntimeConfig();
const userAuth = useCookie("auth.token");

const firstLoad = ref(true);
const allNotifications = ref([]);
const allPage = ref(1);
const allTotal = ref(0);
const hasFetchedAll = ref(false);
const allEl = ref(null);

const isDropdownOpen = ref(false);
const dialogVisible = ref(false);
const toast = useToast();
const selectedApplicationId = ref();

const unreadCount = computed(
  () => allNotifications.value.filter((n) => n.read_at === null).length,
);

const dedupeById = (arr) =>
  Object.values(
    arr.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {}),
  );

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const fetchAllNotifications = async () => {
  try {
    const response = await fetch(
      `${config.public.apiUrl}notifications?page=${allPage.value}`,
      {
        headers: {
          Authorization: `Bearer ${userAuth.value}`,
          "X-Frontend-Portal": config.public.portal,
        },
        method: "GET",
      },
    );

    const data = await response.json();

    if (allPage.value === 1) {
      allNotifications.value = dedupeById(data?.data || []);
      allTotal.value = data?.total ?? 0;
    } else {
      allNotifications.value = dedupeById([
        ...allNotifications.value,
        ...(data?.data || []),
      ]);
    }

    allPage.value += 1;
    if (allNotifications.value.length + 1 >= allTotal.value) {
      hasFetchedAll.value = true;
    }
  } catch (error) {
    console.error("Error fetching all notifications:", error);
  }
};

const onMarkAsRead = async (notification) => {
  if (notification?.data?.deleted === "delete") {
    toast.add({
      severity: "info",
      summary: "Application deleted",
      detail: "This notification has been deleted.",
      life: 4000,
    });
  }

  if (!notification.read_at) {
    await useFetch(
      `${config.public.apiUrl}notifications/mark-as-read/${notification.id}`,
      {
        headers: {
          Authorization: `Bearer ${userAuth.value}`,
        },
        method: "POST",
        onRequest() {
          notification.read_at = new Date().toISOString();
        },
        onResponseError() {
          notification.read_at = null;
        },
      },
    );
  }

  selectedApplicationId.value = notification.data.application_id;
  dialogVisible.value = true;
};

useInfiniteScroll(allEl, fetchAllNotifications, {
  distance: 100,
  canLoadMore: () => !hasFetchedAll.value,
});

onMounted(() => {
  if (firstLoad.value) {
    fetchAllNotifications();
    firstLoad.value = false;
  }
});
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <button
      @click="toggleDropdown"
      class="relative text-white md:bg-primary-700 border-0 md:px-2.5 py-2 rounded-lg md:hover:bg-primary-600"
    >
      <i class="pi pi-bell"></i>
      <span
        v-if="unreadCount > 0"
        class="absolute top-0 -right-2 md:-right-2 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full px-1"
      >
        {{ unreadCount }}
      </span>
    </button>

    <!-- Notification Dropdown -->
    <div
      v-if="isDropdownOpen"
      ref="allEl"
      class="z-50 bg-white shadow-lg overflow-y-scroll border sm:absolute sm:top-6 sm:-right-14 sm:mt-2 sm:w-[400px] sm:rounded-lg max-h-[500px] fixed top-[56px] inset-x-0 bottom-0 sm:inset-auto"
    >
      <div
        v-if="allNotifications?.length === 0"
        class="p-4 text-center text-gray-500"
      >
        No notifications
      </div>

      <div
        v-for="notification in allNotifications"
        :key="notification.id"
        @click="onMarkAsRead(notification)"
        class="cursor-pointer px-4 py-2 flex items-center text-black gap-x-2 hover:bg-blue-100"
        :class="{
          'bg-blue-50': !notification.read_at,
          'bg-white': notification.read_at,
        }"
      >
        <Avatar
          v-if="notification?.data?.profile_url"
          :image="notification.data.profile_url"
          size="large"
          shape="circle"
          class="mr-2"
        />
        <Avatar
          v-else
          icon="pi pi-user"
          size="large"
          shape="circle"
          class="mr-2"
        />

        <div class="flex-1">
          <p class="text-sm" v-html="notification.data.message"></p>

          <div class="flex items-center gap-x-2 text-xs mt-1 text-gray-500">
            <span>{{ notiFormatDate(notification.created_at) }}</span>
          </div>
        </div>

        <span
          v-if="!notification.read_at"
          class="w-2 h-2 bg-green-500 rounded-full"
        ></span>
      </div>
    </div>
  </div>

  <!-- application dialog modal -->
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :showHeader="false"
    :style="{ width: '97vw', height: '98vh' }"
    :pt="{ header: 'py-5 px-8 flex gap-4', footer: 'py-5 px-8 pt-0' }"
  >
    <StudentApplication
      :applicationId="selectedApplicationId"
      @closeModal="dialogVisible = false"
    />
    <template #footer>
      <ApplicationActionBtn :applicationId="selectedApplicationId" />
    </template>
  </Dialog>
</template>
